import validator from 'validator';

// Simplified URL utilities for use with the new validation API

const normalizeUrl = (url: string): string => {
  if (!url) return '';
  
  const trimmed = url.trim();
  
  // If it already has a protocol, return as-is
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }
  
  // Default to https:// for new URLs
  return `https://${trimmed}`;
};

const extractDomain = (url: string): string => {
  if (!url) return '';
  
  try {
    // Remove protocol if present
    const withoutProtocol = url.replace(/^https?:\/\//, '');
    // Remove path, query, and fragment
    const domain = withoutProtocol.split('/')[0].split('?')[0].split('#')[0];
    return domain;
  } catch {
    return url;
  }
};

const isValidUrlFormat = (url: string): boolean => {
  if (!url) return false;
  
  const normalized = normalizeUrl(url);
  return validator.isURL(normalized, {
    protocols: ['http', 'https'],
    require_protocol: true,
    require_valid_protocol: true,
    allow_underscores: true
  });
};

// Smart input validation for typing states
const shouldSkipValidation = (input: string): boolean => {
  if (!input) return false;
  
  const trimmed = input.trim();
  
  // Skip validation only if it looks like user is actively typing a protocol
  // but don't skip for complete protocols like "http://" or "https://"
  if (/^h$|^ht$|^htt$|^http$|^https$|^http:$|^https:$|^http:\/$|^https:\/$$/i.test(trimmed)) {
    return true;
  }
  
  // Skip validation if input doesn't contain a dot (no domain yet)
  if (!trimmed.includes('.')) {
    return true;
  }
  
  return false;
};

const hasProtocolTypo = (input: string): string | null => {
  if (!input) return null;
  
  const trimmed = input.trim();
  
  // Only check for typos if it doesn't look like a valid URL attempt
  if (trimmed.includes('.')) return null;
  
  // Check for common protocol typos - simplified
  if (/^h[tp]+/i.test(trimmed) && !/^https?$/i.test(trimmed)) {
    return 'Invalid protocol format. Did you mean "https://" or "http://"?';
  }
  
  return null;
};

const getProtocolValidation = (input: string): { skip: boolean; error: string | null } => {
  return {
    skip: shouldSkipValidation(input),
    error: hasProtocolTypo(input)
  };
};

// Legacy exports for backward compatibility
const removeProtocol = (url: string | undefined) => {
  if (url === undefined) return { url: '', protocol: 'https://' };

  const matches = url.match(/https?:\/\//g);
  const protocol = matches ? matches[matches.length - 1] : 'https://';

  return {
    url: url.replace(/https?:\/\//g, ''),
    protocol,
  };
};

const addProtocol = (url: string, protocol: string = 'https://'): string => {
  return normalizeUrl(url);
};

const isValidUrl = (url: string) => {
  return isValidUrlFormat(url);
};

export { 
  normalizeUrl, 
  extractDomain, 
  isValidUrlFormat,
  getProtocolValidation,
  shouldSkipValidation,
  hasProtocolTypo,
  // Legacy exports
  removeProtocol, 
  addProtocol, 
  isValidUrl 
};
